import React, { useContext, useEffect, useState } from 'react';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import {
  EditTimeslotCarrierInDTO,
  LocationsEditJobProgressOutDTO,
  LocationTimeslotFormInDTO,
} from '@api/ringil-api.v2';
import { useImmer } from 'use-immer';
import { formatCarrierLocation, isNullOrUndefined } from '@utils/utils';
import dayjs from 'dayjs';
import {
  apiPragueFromDayjsUtc,
  apiUtcFromDayjs,
  applyTimeToUtcDayjsInTz,
  changeDatePreserveTimeInTz,
  dateFromDayjsInTz,
  dayjsFromApiUtcInTz,
  dayjsFromDateAtMidnightInTz,
  formatDateFromDayjsWithLocale,
  formatTime24hFromDayjs,
  formatTimeIntervalFromStringInTzWithLocale,
  shortDateFormat,
} from '@utils/dateWithTz.tsx';
import { createTimeslotNowInTz } from '@utils/timeslotDateTime.tsx';
import { InputMask } from 'primereact/inputmask';
import { ApiContext } from '@api/api';
import useValidation from '@hooks/validation-hook/useValidation';
import * as yup from 'yup';
import ValidationDiv from '@utils/validation';
import { useTranslation } from 'react-i18next';
import TimeslotsProgressTitle from '@components/obsolete/TimeslotsProgress/TimeslotsProgressTitle.tsx';
import TimeslotsProgressInput from '@components/obsolete/TimeslotsProgress/TimeslotsProgressInput.tsx';
import TimeslotsProgressInputSelect from '@components/obsolete/TimeslotsProgress/TimeslotsProgressInputSelect.tsx';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  companyId: string;
  applicationId: string;
  disableCarrierProgressTimeslots?: boolean;
  onUpdateSlot: (slots: EditTimeslotCarrierInDTO) => void;
}

interface Slot {
  locationId: string;
  since: dayjs.Dayjs;
  till: dayjs.Dayjs;
  timezoneId: string;
}

const EditCarrierTimeslot: React.FC<ComponentProps> = ({ visible, onHide, applicationId, companyId, disableCarrierProgressTimeslots, onUpdateSlot }) => {
  const { t } = useTranslation('common');
  const { caGetEditTimeslots } = useContext(ApiContext);

  const [state, setState] = useImmer<LocationsEditJobProgressOutDTO[]>(undefined);
  const [newSlots, setNewSlots] = useState<Slot[]>([]);

  useEffect(() => {
    if (visible) {
      caGetEditTimeslots(companyId, applicationId, data => {
        setState(draft => {
          return data.locations;
        });
        setNewSlots([]);
      });
    }
  }, [visible]);

  const validationSchema = yup.array().of(
    yup.object().shape({
      test: yup.mixed().test({
        name: 'intervals.not.valid',
        message: 'intervals.not.valid',
        test: (value: any, testContext) => {
          const v = testContext.parent as Slot;
          if (isNullOrUndefined(v)) return false;
          // TODO: implement  better checks
          if (isNullOrUndefined(v.since)) return false;
          if (isNullOrUndefined(v.till)) return false;
          if (v.since > v.till) return false;
          return true;
        },
      }),
    }),
  );

  const { clear, validateAndSend, validationErrors, find } = useValidation(validationSchema, () => onContinue());

  const getNewSlot = (locationId: string): Slot | undefined => {
    return newSlots.find(s => s.locationId === locationId);
  };

  const createCustomSlot = (locationId: string, timezoneId: string) => {
    // Get current time in the location's timezone, then convert to UTC for storage
    const { sinceUtc, tillUtc } = createTimeslotNowInTz(timezoneId, 30);

    setNewSlots([...newSlots, { locationId, since: sinceUtc, till: tillUtc, timezoneId }]);
    setState(draft => {
      const locationInfo = draft.find(value => value.id == locationId);
      locationInfo.timeslots.forEach(s => (s.isSelected = false));
    });
  };

  const setCustomerSlot = (locationId: string, slotId: string) => {
    setNewSlots(newSlots.filter(s => s.locationId !== locationId));
    setState(draft => {
      const locationInfo = draft.find(value => value.id == locationId);
      locationInfo.timeslots.forEach(s => (s.isSelected = false));
      locationInfo.timeslots.find(s => s.id == slotId).isSelected = true;
    });
  };

  const changeCustomSlotDate = (locationId: string, selectedDay: dayjs.Dayjs) => {
    const customSlot = newSlots.find(s => s.locationId === locationId);
    const ret = newSlots.filter(s => s.locationId !== locationId);

    // Use helper to preserve time when changing date
    const newSinceUtc = changeDatePreserveTimeInTz(customSlot.since, selectedDay, customSlot.timezoneId);
    const newTillUtc = changeDatePreserveTimeInTz(customSlot.till, selectedDay, customSlot.timezoneId);

    setNewSlots([
      ...ret,
      {
        ...customSlot,
        since: newSinceUtc,
        till: newTillUtc,
      },
    ]);
  };

  const changeSince = (locationId: string, time: string) => {
    const customSlot = newSlots.find(s => s.locationId === locationId);
    const ret = newSlots.filter(s => s.locationId !== locationId);

    // Use helper to apply time through timezone conversion
    const updatedUtc = applyTimeToUtcDayjsInTz(customSlot.since, time, customSlot.timezoneId);

    setNewSlots([
      ...ret,
      {
        ...customSlot,
        since: updatedUtc,
        till: customSlot.till,
      },
    ]);
  };

  const changeTill = (locationId: string, time: string) => {
    const customSlot = newSlots.find(s => s.locationId === locationId);
    const ret = newSlots.filter(s => s.locationId !== locationId);

    // Use helper to apply time through timezone conversion
    const updatedUtc = applyTimeToUtcDayjsInTz(customSlot.till, time, customSlot.timezoneId);

    setNewSlots([
      ...ret,
      {
        ...customSlot,
        since: customSlot.since,
        till: updatedUtc,
      },
    ]);
  };

  const onContinue = () => {
    //const ret = {locationTimeslotIds: } as EditTimeslotCarrierInDTO;

    const carrierSlots = newSlots.map(a => {
      return {
        locationId: a.locationId,
        newTimeslot: {
          timeslotSince: apiPragueFromDayjsUtc(a.since, a.timezoneId),
          timeslotTill: apiPragueFromDayjsUtc(a.till, a.timezoneId),
          utcStart: apiUtcFromDayjs(a.since),
          utcEnd: apiUtcFromDayjs(a.till),
          timezoneId: a.timezoneId,
        },
      } as LocationTimeslotFormInDTO;
    });

    const customerSlots = state
      .map(location => {
        return {
          locationId: location.id,
          timeslots: location.timeslots.filter(lt => lt.isSelected),
        };
      })
      .filter(l => l.timeslots.length === 1)
      .map(l => {
        return {
          locationId: l.locationId,
          timeslotId: l.timeslots[0].id,
        } as LocationTimeslotFormInDTO;
      });

    const compinedSlots = [...carrierSlots, ...customerSlots];
    onUpdateSlot({ locationTimeslotIds: compinedSlots });
  };

  const { i18n } = useTranslation();

  return (
    <>
      {/*// @ts-ignore*/}
      <RingilSidebar
        visible={visible}
        heading={t(`EditCarrierTimeslot.selectTimeslotsForLoadingAndUnloading`, `Select timeslots for loading and unloading`)}
        onContinue={() => validateAndSend(newSlots)}
        onBack={() => onHide()}
        continueLabel={t(`EditCarrierTimeslot.continue`, `Continue`)}
      >
        {state && (
          <>
            <div>
              {/* 1 */}
              {state.map((location, index) => {
                return (
                  <div className="timeslotsProgress" key={index}>
                    <TimeslotsProgressTitle
                      number={`${index + 1}`}
                      address={formatCarrierLocation(location.zipCode, location.city, location.country)}
                      company={location.name}
                      timezoneId={location.timezoneId}
                    />
                    <div className="timeslotsProgress-wrapper">
                      {location.timeslots.map((timeslot, timeslotIndex) => {
                        const slotDayJs = location.timezoneId != null
                          ? dayjsFromApiUtcInTz(timeslot.utcStart, location.timezoneId)
                          : dayjsFromApiUtcInTz(timeslot.sinceInUTC, 'Europe/Prague');
                        const slotDate = formatDateFromDayjsWithLocale(slotDayJs);
                        const sinceString = timeslot.timezoneId != null ? timeslot.utcStart : timeslot.sinceInUTC;
                        const tillString = timeslot.timezoneId != null?  timeslot.utcEnd : timeslot.tillInUTC;
                        const timezoneId = timeslot.timezoneId ?? 'Europe/Prague';
                        const slotTime = formatTimeIntervalFromStringInTzWithLocale(timeslot.isNotSpecifiedTime, sinceString, tillString, timezoneId, t);
                        return (
                          <TimeslotsProgressInput
                            key={timeslotIndex}
                            date={slotDate}
                            time={slotTime}
                            checked={timeslot.isSelected}
                            onCheck={() => setCustomerSlot(location.id, timeslot.id)}
                          />
                        );
                      })}

                      {getNewSlot(location.id) && (
                        <>
                          <TimeslotsProgressInputSelect inputName="dataTime1" inputValue="data-time-4" checked={true}>
                            <div className="timeslotsProgress-calendar">
                            <Calendar
                              locale={i18n.language}
                              value={dateFromDayjsInTz(getNewSlot(location.id).since, location.timezoneId ?? 'Europe/Prague') as Date}
                              dateFormat={shortDateFormat}
                              onChange={e => {
                                const selectedDay = dayjsFromDateAtMidnightInTz(e.value as Date, location.timezoneId ?? 'Europe/Prague') as dayjs.Dayjs;
                                // PrimeReact fires onChange(null) while the user types an unparseable date into the
                                // input. Keep the date already selected instead of propagating the null, which would
                                // reach changeDatePreserveTimeInTz as both since and till. (Sentry RINGIL-APP-QG.)
                                if (isNullOrUndefined(selectedDay)) return;
                                changeCustomSlotDate(location.id, selectedDay);
                              }}
                            />
                            </div>
                            <div className="p-fluid timeslotsProgress-input">
                              <InputMask mask={'99:99'}
                                         value={formatTime24hFromDayjs(getNewSlot(location.id).since.tz(location.timezoneId))}
                                         onComplete={e => changeSince(location.id, e.value)} />
                            </div>
                            <div className="delimiter"> —</div>
                            <div className="p-fluid timeslotsProgress-input">
                              <InputMask mask={'99:99'}
                                         value={formatTime24hFromDayjs(getNewSlot(location.id).till.tz(location.timezoneId))}
                                         onComplete={e => changeTill(location.id, e.value)} />
                            </div>
                            <ValidationDiv errors={validationErrors} path={`[${index}].test`} />
                          </TimeslotsProgressInputSelect>
                        </>
                      )}
                    </div>
                    {!getNewSlot(location.id) && !disableCarrierProgressTimeslots && (
                      <Button
                        icon="icon pi pi-plus"
                        className="btn-plus p-mt-2 p-button-outlined"
                        label={t(`EditCarrierTimeslot.pickASpecificTime`, `Pick a specific time`)}
                        onClick={() => createCustomSlot(location.id, location.timezoneId) }
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </RingilSidebar>
    </>
  );
};

export default EditCarrierTimeslot;
